import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css";
import { BrowserRouter } from "react-router-dom";
import HostApi, { PlutoniosChatApi } from "./utils/HostApi";
import SessionContext from "./utils/SessionContext";
import HostLanguage from "./utils/HostLanguage";
import Modes from "./utils/Modes";
import { loadFixtures } from "./fixtures";

// Expose the `init` function globally
declare global {
  interface Window {
    ChatWidget: {
      init: (options: {
        container: HTMLElement;
        a_id?: string;
        a_name?: string;
        isEditor?: boolean;
        editorStyle?: any;
        language?: any;
        /**
         * Track `<html lang>` after the mount. The embed loader sets it unless
         * the hotel pinned a language in the snippet; older bundles ignore it.
         */
        followPageLanguage?: boolean;
        mode?: string;
      }) => void;
    };
    PlutoniosChat: PlutoniosChatApi;
    /** Set by the embed script (CDNController) before this bundle loads. */
    __PLUTONIOS_EMBED__?: boolean;
    /** Set by public/legacy/main-shim.js: the page asked for a retired bundle. */
    __PLUTONIOS_LEGACY_EMBED__?: string | boolean;
  }
}

/**
 * The standalone page lives under /chat on the widget host, either as
 * `/chat?a_id=<uuid>` or as `/chat/<uuid>` — Firebase rewrites everything to
 * index.html, so both reach this bundle.
 */
const STANDALONE_PATH = /^\/(chat|c)(\/|$)/;

function readStandaloneRoute() {
  const params = new URLSearchParams(window.location.search);
  const path = window.location.pathname;

  const pathId = path.match(/^\/(?:chat|c)\/([^/?#]+)/)?.[1];
  const a_id = params.get("a_id") || (pathId ? decodeURIComponent(pathId) : undefined);

  return {
    a_id,
    a_name: params.get("a_name") || undefined,
    language: params.get("language") || params.get("lang") || undefined,
    // Everything the widget does not consume itself is context for the
    // assistant: ?room=214&floor=2 tells it where the guest is standing.
    context: SessionContext.fromSearchParams(params),
    // The embed script marks the page it loaded into. Without that marker a
    // customer site that happens to serve /chat?a_id=… would mount a second,
    // uncontained copy of the widget on top of the embedded one.
    isStandalone: !window.__PLUTONIOS_EMBED__ && STANDALONE_PATH.test(path) && !!a_id,
    // Legacy local-dev entry, unchanged: mounts into #root as a plain widget.
    isLocal: params.get("isLocal") === "true",
  };
}

function renderApp(
  container: HTMLElement,
  a_id?: string,
  a_name?: string,
  isEditor?: boolean,
  editorStyle?: any,
  language?: any,
  mode?: string
) {
  if (!container) {
    console.error("ChatWidget: Container is undefined or null.");
    return;
  }

  ReactDOM.createRoot(container).render(
    <BrowserRouter>
      <App
        assistant={{ id: a_id, name: a_name }}
        isEditor={isEditor}
        editorStyle={editorStyle}
        language={language}
        mode={mode}
      />
    </BrowserRouter>
  );
}

// Attach `init` function to the window object
window.ChatWidget = {
  init: ({
    container,
    a_id,
    a_name,
    isEditor,
    editorStyle,
    language,
    followPageLanguage,
    mode,
  }) => {
    if (followPageLanguage && !isEditor) HostLanguage.followDocument();
    renderApp(container, a_id, a_name, isEditor, editorStyle, language, mode);
  },
};

// The host-page control surface, published as early as the bundle runs. It
// queues until the widget mounts, so a click that lands mid-download is kept
// rather than dropped.
HostApi.install();

// URL fixtures for the dev preview (?fixture=<name>): the preview is a
// production build pointed at dev data, so it has no dev-only hooks — this
// runs the same bundle on /chat and shadow-test.html.
loadFixtures();

const route = readStandaloneRoute();

// Dev-only: expose the slider store so local tooling can inject template
// fixtures without going through a live assistant reply. Not tied to the
// standalone branch — the bugs worth chasing (shadow DOM, host-page CSS) only
// show up when the widget is mounted through init() the way a customer site
// does it.
if (process.env.NODE_ENV === "development") {
  import("./widgets/Chat/components/slider/SliderState").then((m) => {
    (window as any).__sliderState = m.useSliderState;
  });
  // Same idea for the booking flow: the store to inspect/drive it, and the
  // switch that makes it reachable at all. isBookingEnabled() reads this only
  // in development — a production bundle ignores it.
  import("./widgets/Chat/components/slider/booking/BookingState").then((m) => {
    (window as any).__bookingState = m.useBookingState;
  });
}

if (route.isStandalone) {
  const container = document.getElementById("root");

  if (container) {
    SessionContext.set(route.context);
    document.documentElement.classList.add("plutonios-standalone");
    renderApp(
      container,
      route.a_id,
      route.a_name,
      false,
      undefined,
      route.language,
      Modes.STANDALONE
    );
  } else {
    console.error("Standalone mode: No root container found.");
  }
} else if (route.isLocal) {
  const container = document.getElementById("root");

  if (container) {
    SessionContext.set(route.context);
    renderApp(container, route.a_id, route.a_name, false, undefined, route.language);
  } else {
    console.error("Standalone mode: No root container found.");
  }
}
